import { thaloColors } from '@/styles/thalo-colors'
import formatValueAndUnit from '@/metrics/formatValueAndUnit'
import { LineChartResponse } from '@/components/charts/lineChartResponse'
import { resolveSeriesType } from '@/components/charts/seriesTypes'

// ── Grid ──────────────────────────────────────────────────────────────────────

export function buildChartGrid({ showZoom }: { showZoom: boolean }) {
  return {
    top: '10%',
    left: '3%',
    right: '5%',
    bottom: showZoom ? '15%' : '5%',
    containLabel: true,
    backgroundColor: '#ffffff',
  }
}

// ── X-Axis ────────────────────────────────────────────────────────────────────

export function buildChartXAxis({
  results,
  fontSize,
}: {
  results: LineChartResponse[]
  fontSize: number
}) {
  const xAxisBase = results.reduce((_, r) => r.xAxis, {} as any)
  const resultsWithData = results.filter(r => r.series.some(s => s.data.length > 0))

  return {
    ...xAxisBase,
    type: 'time' as const,
    boundaryGap: ["0%", "0%"],
    axisLabel: {
      hideOverlap: true,
      color: '#000000',
      fontSize,
    },
    axisLine: { lineStyle: { color: '#000000' } },
    axisTick: { lineStyle: { color: '#000000' } },
    splitNumber: 4,
    min: resultsWithData.length > 0
      ? Math.max(
          ...resultsWithData.map(r =>
            Math.min(...r.series.map(s => new Date(s.data[0][0]).getTime()))
          )
        )
      : undefined,
    max: resultsWithData.length > 0
      ? Math.min(
          ...resultsWithData.map(r =>
            Math.max(...r.series.map(s => new Date(s.data[s.data.length - 1][0]).getTime()))
          )
        )
      : undefined,
  }
}

// ── Y-Axis ────────────────────────────────────────────────────────────────────

export function buildChartYAxis({
  results,
  fontSize,
  unit,
  overrides,
  yAxisName,
  clampYAxisToData,
}: {
  results: LineChartResponse[]
  fontSize: number
  unit?: string
  overrides?: { chart_unit?: string }
  yAxisName?: string
  clampYAxisToData: boolean
}) {
  return results.map(r => r.yAxis).map((axis: any) => {
    const formatter = (value: any) => {
      const rounded = value.toLocaleString(undefined, { maximumFractionDigits: 2 })
      if (overrides?.chart_unit) return `${rounded} ${overrides.chart_unit}`
      if (axis.unit) return formatValueAndUnit(rounded, axis.unit).join(' ')
      if (unit) return formatValueAndUnit(rounded, unit).join(' ')
      return rounded
    }

    const axisOptions = {
      axisLabel: { formatter, color: '#000000', fontSize, margin: 8 },
      name: axis.name ?? yAxisName ?? '',
      nameLocation: 'middle' as const,
      nameGap: fontSize < 16 ? 70 : 100,
      nameTextStyle: { fontSize: fontSize + 2, fontWeight: 500, color: '#000000' },
      alignTicks: true,
      axisLine: { lineStyle: { color: '#000000' } },
      axisTick: { lineStyle: { color: '#000000' } },
      splitLine: { lineStyle: { color: 'rgba(0, 0, 0, 0.1)' } },
    }

    return clampYAxisToData
      ? { ...axisOptions, min: 'dataMin', max: 'dataMax' }
      : axisOptions
  })
}

// ── Legend ────────────────────────────────────────────────────────────────────

export function buildChartLegend({
  results,
  fontSize,
}: {
  results: LineChartResponse[]
  fontSize: number
}) {
  return {
    data: results.flatMap(r => r.series.map(s => s.name)),
    top: 0,
    right: 0,
    bottom: 20,
    icon: 'circle',
    itemWidth: 10,
    textStyle: {
      color: '#000000',
      fontFamily: 'Barlow Semi Condensed',
      fontSize,
    },
  }
}

// ── Tooltip ───────────────────────────────────────────────────────────────────

export function buildChartTooltipConfig({ showTooltip }: { showTooltip: boolean }) {
  if (!showTooltip) return { show: false }

  return {
    confine: true,
    trigger: 'axis' as const,
    axisPointer: { type: 'cross' },
    formatter: (params: any[]) => {
      const visible = params.filter(
        p => resolveSeriesType(p.seriesName ?? '').tooltipVisibility !== 'hidden'
      )
      const nameOnly = visible.filter(
        p => resolveSeriesType(p.seriesName ?? '').tooltipVisibility === 'name-only'
      )
      if (nameOnly.length > 0) {
        return nameOnly.map(p => p.seriesName).join('<br/>')
      }
      return visible
        .filter(p => p.value?.[1] != null)
        .map(p => `${p.seriesName}: ${Number(p.value[1]).toFixed(1)}`)
        .join('<br/>')
    },
  }
}

// ── Text style (shared) ───────────────────────────────────────────────────────

export const chartTextStyle = {
  fontFamily: 'Barlow Semi Condensed',
  color: thaloColors.primary.deepNavy,
}
